import { LegalPage } from './LegalPage';

const CONTENT = `# Terms of Use

**Last updated: 08 July 2026**

These Terms of Use ("**Terms**") govern access to and use of the Taiga platform, together with the website at taigahedge.com and any related applications, interfaces, APIs and documentation (collectively, the "**Platform**").

The Platform is operated by **Forsa Ltd** ("**Forsa**", "**we**", "**us**", "**our**"), a company registered in England and Wales under company number **17288614**, with its registered office at 71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom. "**Taiga**" is a trading name used by Forsa Ltd for the Platform.

By accessing or using the Platform, you ("**you**", "**your**", the "**Client**") agree to be bound by these Terms. If you do not agree, you must not access or use the Platform.

---

## 1. Nature of the Platform

1.1 Taiga is a **risk-management technology platform** provided to institutional and business users, including foreign-exchange (FX) and contract-for-difference (CFD) brokers and their authorised operators. The Platform provides tools for monitoring exposure, configuring hedging rules, viewing price and liquidity-provider data, portfolio analytics, reporting and related operational functions.

1.2 **The Platform is software.** It is a tool that presents information and executes configurations that you define. It does not, and is not intended to, provide investment advice, financial advice, trading recommendations, or any regulated financial service.

1.3 Forsa is a technology provider. Unless expressly stated otherwise in writing, Forsa does not act as a broker, dealer, principal, counterparty, custodian, or investment manager, and does not execute trades on your behalf. Any orders, hedges or transactions ultimately routed through your own systems, liquidity providers or execution venues remain your sole responsibility.

1.4 **Deployment on your own infrastructure.** Taiga is licensed software that is installed, deployed and operated on the Client's own infrastructure. It is **not** provided as a hosted, cloud, software-as-a-service (SaaS) or platform-as-a-service (PaaS) offering. Forsa does not host the Platform and does not host, store, or have routine access to the Client's operational or trading data, or to the personal data processed within the deployed instance. The Client is responsible for the provisioning, security, availability, backup and operation of the environment in which the Platform runs.

1.5 **Regulatory status.** Forsa is a technology provider. It supplies software and does not carry on any regulated financial-services activity, provide investment or financial advice, execute transactions, act as counterparty, or hold client funds. Forsa is not required to hold, and does not hold, authorisation from the Financial Conduct Authority or any other financial regulator.

---

## 2. Eligibility and business use

2.1 The Platform is intended for use **by businesses and their personnel acting in a professional capacity**, and not by consumers. By using the Platform you represent that you are accessing it for business purposes and are authorised to bind the Client organisation to these Terms.

2.2 You represent and warrant that you and your organisation hold all licences, authorisations and regulatory permissions required to conduct your own business, and that your use of the Platform will comply with all applicable laws and regulations in every jurisdiction in which you operate.

2.3 The Platform is not directed at any person in any jurisdiction where its provision or use would be contrary to law. You are responsible for determining whether your use of the Platform is lawful in your jurisdiction.

---

## 3. Accounts, credentials and access

3.1 Access to the Platform is controlled **within the Client's deployed instance**. User accounts and access credentials — which may include multi-factor authentication — are provisioned and administered by the Client's own administrators within the Client's environment.

3.2 The Client is responsible for the confidentiality of all credentials, for all activity carried out under accounts within its instance, and for granting, reviewing and revoking access appropriately.

3.3 You must not permit access by unauthorised persons, and must not circumvent, or permit the circumvention of, any access controls, authentication mechanisms, rate limits or licensing controls within the Platform.

3.4 You must notify us without undue delay of any suspected security vulnerability in the Platform software.

---

## 4. Acceptable use

4.1 You agree not to, and not to permit any person to:

- (a) use the Platform for any unlawful, fraudulent or abusive purpose;
- (b) probe, scan or test the vulnerability of the Platform software except with our prior written consent;
- (c) introduce any malware into the Platform, or use it to attempt unauthorised access to any systems, data or networks;
- (d) copy, decompile, reverse-engineer, or attempt to derive the source code of the Platform, except to the extent this restriction is prohibited by applicable law;
- (e) resell, sublicense, distribute, or make the Platform available to any third party except as expressly permitted in writing;
- (f) interfere with or disrupt the integrity of the Platform software or its licensing controls;
- (g) circumvent any usage, seat or licensing limits; or
- (h) remove, obscure or alter any proprietary notices.

---

## 5. Data, market information and third-party feeds

5.1 The Platform, as deployed in your environment, displays market data, prices, liquidity-provider quotes, and other information, some of which originates from third parties. Such information is provided **"as is" and "as available"**.

5.2 We do not warrant the accuracy, completeness, timeliness, or fitness for any purpose of any market data or third-party information. Data may be delayed, incomplete, or subject to error, interruption, or the terms of the originating provider.

5.3 You must not rely on any single figure, indicator or output of the Platform as the sole basis for any operational, trading, hedging or risk decision. You are responsible for independently verifying information before acting on it. See the separate **Risk Disclosure**.

---

## 6. Intellectual property and licence

6.1 The Platform, including all software, interfaces, designs, text, graphics, documentation and the Taiga name and logos, is owned by or licensed to Forsa and is protected by intellectual-property laws. All rights not expressly granted are reserved.

6.2 Subject to these Terms and any applicable order form or agreement, we grant you a non-exclusive, non-transferable, revocable licence to install and use the Platform on your own infrastructure for your internal business purposes for the duration of your subscription or licence term.

6.3 The licence does not transfer any ownership in the Platform. You obtain only the rights expressly granted. Any feedback you provide may be used by us without restriction or obligation to you.

---

## 7. Client data

7.1 As between the parties, you retain all rights in the data you or your users submit to, or generate through, the Platform ("**Client Data**").

7.2 Client Data resides within your own infrastructure and remains under your control at all times. Forsa does not host, store, or have routine access to Client Data.

7.3 Where you request support, maintenance or professional services that require Forsa to access your deployed instance, any personal data Forsa thereby accesses is processed solely on your documented instructions and under the data-processing terms agreed between the parties, under which you act as controller and Forsa acts as processor. Forsa does not otherwise process Client Data.

7.4 You are responsible for the accuracy, quality and legality of Client Data and for having obtained all rights and consents necessary to process it.

---

## 8. Availability, updates and changes

8.1 Because the Platform runs on your own infrastructure, the availability, performance and continuity of any deployed instance depend on your environment and are your responsibility.

8.2 Forsa provides the Platform software and may issue updates, fixes and new releases. Any deployment, upgrade, support or service-level commitments apply only where expressly agreed in a separate written agreement or order form.

8.3 We may modify, enhance, or discontinue features in future releases. Where a change is material and adverse to your use, we will use reasonable efforts to give advance notice through our release process.

---

## 9. Warranties and disclaimers

9.1 Except as expressly stated in a separate written agreement, the Platform is provided **"as is" and "as available"**, and we disclaim all warranties, conditions and representations of any kind, whether express, implied or statutory, to the fullest extent permitted by law, including any implied warranties of satisfactory quality, fitness for a particular purpose, and non-infringement.

9.2 We do not warrant that the Platform will meet your requirements, that its outputs will be accurate or reliable, or that defects will be corrected.

---

## 10. Limitation of liability

10.1 Nothing in these Terms excludes or limits any liability that cannot be excluded or limited under applicable law, including liability for death or personal injury caused by negligence, or for fraud or fraudulent misrepresentation.

10.2 Subject to clause 10.1, we shall not be liable, whether in contract, tort (including negligence), breach of statutory duty or otherwise, for any:

- (a) loss of profits, revenue, business, goodwill, or anticipated savings;
- (b) trading, hedging or investment losses;
- (c) loss of or corruption of data;
- (d) loss arising from your reliance on data, prices, outputs or configurations of the Platform;
- (e) loss arising from the operation, security or availability of the infrastructure on which you deploy the Platform; or
- (f) indirect, special or consequential loss,

in each case however arising.

10.3 Subject to clause 10.1, Forsa shall have **no liability** to you arising out of or in connection with these Terms or the Platform, to the fullest extent permitted by law. To the extent any liability cannot be wholly excluded, our total aggregate liability for all claims shall not exceed **£100**.

10.4 You acknowledge that the Platform is a tool that you operate on your own infrastructure, and that responsibility for all operational, trading, hedging and risk decisions, and for your deployment environment, rests with you. The allocation of risk in this clause reflects the fees charged and is a fundamental basis of the parties' bargain.

---

## 11. Indemnity

You agree to indemnify and hold harmless Forsa and its officers, employees and agents against all claims, losses, liabilities, damages, costs and expenses (including reasonable legal fees) arising out of or in connection with your breach of these Terms, your misuse of the Platform, your Client Data, your deployment environment, or your own regulatory or trading activities.

---

## 12. Confidentiality

Each party may receive confidential information of the other. Each party agrees to keep the other's confidential information confidential, to use it only for the purposes of these Terms, and to protect it with reasonable care. This obligation does not apply to information that is public through no fault of the receiving party, is independently developed, or is required to be disclosed by law or regulator.

---

## 13. Term, licence termination

13.1 These Terms apply for as long as you access or use the Platform, and alongside any separate subscription agreement or order form.

13.2 We may terminate the licence and your right to use the Platform with immediate effect if you materially breach these Terms, if required by law or a regulator, or where necessary to protect our rights or intellectual property. On termination you must cease all use of the Platform and, where required by the applicable licence or agreement, remove or destroy the software.

13.3 Clauses that by their nature should survive termination (including those relating to intellectual property, confidentiality, liability, indemnity and governing law) will continue to apply.

---

## 14. Changes to these Terms

We may update these Terms from time to time. The current version will be posted on the Platform with the "Last updated" date. Where changes are material, we will take reasonable steps to notify you. Your continued use of the Platform after changes take effect constitutes acceptance of the updated Terms.

---

## 15. Governing law and jurisdiction

15.1 These Terms and any dispute or claim arising out of or in connection with them (including non-contractual disputes) are governed by the laws of **England and Wales**.

15.2 The courts of England and Wales have exclusive jurisdiction to settle any such dispute or claim.

---

## 16. General

16.1 If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions continue in effect.

16.2 A failure to enforce any provision is not a waiver of it.

16.3 You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations to an affiliate or successor.

16.4 These Terms, together with any applicable order form, subscription agreement, Privacy Policy and Risk Disclosure, constitute the entire agreement between the parties in respect of the Platform.

16.5 A person who is not a party to these Terms has no rights under the Contracts (Rights of Third Parties) Act 1999 to enforce any of its terms.

---

## 17. Contact

**Forsa Ltd** (trading as Taiga)
71–75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom
Company number: 17288614
Telephone: +1 888 301 6002
Email: info@taigahedge.com
`;

export function TermsPage() {
  return <LegalPage content={CONTENT} />;
}